import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { messagesKey, turnStatusKey } from '../constants';
import { requestTurnCancellation, useTurnStatusQuery } from '../api';
import { useAgentForgeStore } from '../stores';

const TURN_STATUS_POLL_MS = 5_000;
const CANCEL_RETRY_INITIAL_MS = 1_000;
const CANCEL_RETRY_MAX_MS = 30_000;
const CANCEL_REQUEST_TIMEOUT_MS = 10_000;
const UNIDENTIFIED_TERMINAL_GRACE_MS = 150_000;
const SUBMISSION_VISIBILITY_GRACE_MS = UNIDENTIFIED_TERMINAL_GRACE_MS;
const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'timed_out',
]);

const recoveryPhase = (
  phase: 'submitting' | 'running' | 'recovering' | 'cancelling' | 'finalizing'
): 'submitting' | 'recovering' | 'cancelling' =>
  phase === 'submitting'
    ? 'submitting'
    : phase === 'cancelling'
      ? 'cancelling'
      : 'recovering';

const terminalTooEarlyForUnidentifiedTurn = (
  current: {
    turnId?: string;
    workflowId?: string;
    phase: string;
    startedAt: string;
  },
  status: {
    status: string;
    turn_id?: string;
    workflow_id?: string;
    identity_mode?: string;
  }
): boolean => {
  const exactIdentityMatches =
    !!current.turnId &&
    status.identity_mode === 'exact' &&
    status.turn_id === current.turnId;
  const workflowIdentityMatches =
    !!current.workflowId && status.workflow_id === current.workflowId;
  if (
    exactIdentityMatches ||
    workflowIdentityMatches ||
    !TERMINAL_STATUSES.has(status.status)
  )
    return false;
  if (current.phase === 'finalizing') return false;
  const startedAt = Date.parse(current.startedAt);
  // A previous workflow can finish (and update its DB projection) after this
  // browser begins uploading the next turn, so comparing two wall clocks is
  // not enough to establish identity. Give the backend's two-minute start
  // visibility window time to expose a running row / let the POST return its
  // id. The extra 30s absorbs skew; after that, accept the durable terminal so
  // an actually-lost acknowledgement cannot wedge the composer forever.
  return (
    !Number.isFinite(startedAt) ||
    Date.now() - startedAt < UNIDENTIFIED_TERMINAL_GRACE_MS
  );
};

/** Reconciles one session's persisted browser turn with Temporal's canonical
 * execution status. WebSocket events remain the low-latency path; this hook is
 * the deployment/reload path and never interprets silence as completion. */
export const useTurnRecovery = (sessionId: string | null | undefined): void => {
  const queryClient = useQueryClient();
  const activeTurn = useAgentForgeStore((s) =>
    sessionId ? s.activeTurnsBySession[sessionId] : undefined
  );
  const upsertActiveTurn = useAgentForgeStore((s) => s.upsertActiveTurn);
  const resolveActiveTurn = useAgentForgeStore((s) => s.resolveActiveTurn);
  const clearQueuedMessage = useAgentForgeStore((s) => s.clearQueuedMessage);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);

  const query = useTurnStatusQuery({
    sessionId,
    turnId: activeTurn?.turnId,
    identityMode: activeTurn?.identityMode,
    enabled: !!sessionId && !sessionId.startsWith('pending-'),
    // `finalizing` still needs reconciliation: stream_done can precede the
    // durable agent_turn projection becoming terminal during a mixed worker
    // rollout. Keep polling until canonical transcript hydration resolves the
    // active turn; otherwise a metadata-less assistant row can wedge the
    // composer forever.
    refetchIntervalMs: activeTurn ? TURN_STATUS_POLL_MS : undefined,
  });

  useEffect(() => {
    if (!sessionId || activeTurn?.phase !== 'cancelling') return;
    const authoritativeStatus = query.data?.status;
    const authoritativeTurnId = query.data?.turn_id;
    const authoritativeWorkflowId = query.data?.workflow_id;
    const identityMatches =
      (!activeTurn.turnId ||
        !authoritativeTurnId ||
        activeTurn.turnId === authoritativeTurnId) &&
      (!activeTurn.workflowId ||
        !authoritativeWorkflowId ||
        activeTurn.workflowId === authoritativeWorkflowId);
    const shouldRetry =
      identityMatches &&
      (authoritativeStatus === 'running' ||
        (authoritativeStatus === undefined && query.isError));
    if (!shouldRetry) return;

    let disposed = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let requestTimer: ReturnType<typeof setTimeout> | undefined;
    let requestController: AbortController | undefined;
    let attempt = 0;

    const schedule = () => {
      const delay = Math.min(
        CANCEL_RETRY_INITIAL_MS * 2 ** Math.min(attempt, 5),
        CANCEL_RETRY_MAX_MS
      );
      retryTimer = setTimeout(async () => {
        if (disposed) return;
        const current =
          useAgentForgeStore.getState().activeTurnsBySession[sessionId];
        if (!current || current.phase !== 'cancelling') return;
        if (
          activeTurn.turnId &&
          current.turnId &&
          activeTurn.turnId !== current.turnId
        )
          return;
        if (
          activeTurn.workflowId &&
          current.workflowId &&
          activeTurn.workflowId !== current.workflowId
        )
          return;

        requestController = new AbortController();
        requestTimer = setTimeout(
          () => requestController?.abort(),
          CANCEL_REQUEST_TIMEOUT_MS
        );
        try {
          await requestTurnCancellation(
            {
              sessionId,
              turnId: current.turnId,
              identityMode: current.identityMode,
            },
            requestController.signal
          );
        } catch {
          // The POST is idempotent and its acknowledgement can be lost during
          // a rollout. Durable turn status remains the authority; keep retrying
          // with a capped delay until it reports cancelling or terminal.
        } finally {
          if (requestTimer) clearTimeout(requestTimer);
          requestTimer = undefined;
          requestController = undefined;
        }
        if (disposed) return;
        void queryClient.invalidateQueries({
          queryKey: turnStatusKey(
            sessionId,
            current.identityMode === 'legacy' ? undefined : current.turnId
          ),
        });
        attempt += 1;
        schedule();
      }, delay);
    };

    schedule();
    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (requestTimer) clearTimeout(requestTimer);
      requestController?.abort();
    };
  }, [
    sessionId,
    activeTurn?.phase,
    activeTurn?.turnId,
    activeTurn?.identityMode,
    activeTurn?.workflowId,
    query.data?.turn_id,
    query.data?.status,
    query.data?.workflow_id,
    query.isError,
    queryClient,
  ]);

  useEffect(() => {
    if (!sessionId || !query.isError) return;
    const current =
      useAgentForgeStore.getState().activeTurnsBySession[sessionId];
    if (!current || current.phase === 'finalizing') return;
    // 502/503/network errors are expected while services roll. Keep the send
    // gate locked and tell the UI it is recovering; the fixed query interval
    // continues independently of React Query's retry budget.
    upsertActiveTurn(sessionId, {
      phase: recoveryPhase(current.phase),
    });
  }, [sessionId, query.isError, query.errorUpdatedAt, upsertActiveTurn]);

  useEffect(() => {
    const status = query.data;
    if (!sessionId || !status) return;
    const current =
      useAgentForgeStore.getState().activeTurnsBySession[sessionId];
    // Defense in depth for a latest-status response seen during a mixed
    // rollout: a different logical UUID can never take over this local turn.
    // Once exact identity is proven, a late probing/legacy response cannot
    // downgrade it or clear the UUID, even if the old request completed after
    // the query key changed.
    if (
      current?.identityMode === 'exact' &&
      current.turnId &&
      (status.identity_mode !== 'exact' || status.turn_id !== current.turnId)
    )
      return;
    // During a mixed-fleet rollout, an old replica can answer the probing
    // fallback before this browser's POST acknowledgement arrives. Its latest
    // session row is not proof of ownership; wait for the live mutation to
    // establish exact or legacy identity instead of discarding the client UUID.
    if (
      current?.phase === 'submitting' &&
      current.identityMode === 'probing' &&
      current.turnId &&
      status.identity_mode === 'legacy'
    )
      return;
    if (current?.turnId && status.turn_id && current.turnId !== status.turn_id)
      return;

    if (status.status === 'running' || status.status === 'cancelling') {
      // A queued turn stays in FIFO until acceptance is proven. An exact
      // durable status row is that proof even when the original 202 was lost;
      // remove it now so a later workflow failure retries with a fresh logical
      // id instead of replaying an already-recorded failed turn.
      if (
        current?.turnId &&
        status.identity_mode === 'exact' &&
        status.turn_id === current.turnId
      ) {
        clearQueuedMessage(sessionId, current.turnId);
      }
      const workflowChanged = !!(
        current?.workflowId &&
        status.workflow_id &&
        current.workflowId !== status.workflow_id
      );
      if (workflowChanged) {
        // The HTTP row is the durable authority. Replace stale browser state
        // (another tab/version may have started the current turn) and discard
        // the old workflow's partial draft before accepting its identity.
        clearStreamingDraft();
      }
      const hasLiveDraft =
        !workflowChanged &&
        useAgentForgeStore.getState().streamingDraft?.sessionId === sessionId;
      const cancellationMatches =
        current?.phase === 'cancelling' &&
        (!current.workflowId ||
          !status.workflow_id ||
          current.workflowId === status.workflow_id);
      upsertActiveTurn(sessionId, {
        // A status-only running turn after reload remains visibly "resuming"
        // until a WS event proves the live stream has caught up. A normal
        // just-submitted turn can use the regular Thinking treatment.
        phase:
          status.status === 'cancelling' || cancellationMatches
            ? 'cancelling'
            : hasLiveDraft || current?.phase === 'submitting'
              ? 'running'
              : 'recovering',
        turnId:
          status.identity_mode === 'legacy'
            ? undefined
            : (status.turn_id ?? current?.turnId),
        identityMode: status.identity_mode ?? current?.identityMode,
        workflowId: status.workflow_id ?? current?.workflowId,
        runId: status.run_id ?? current?.runId,
        updatedAt: status.updated_at,
      });
      return;
    }

    // A session status probe runs once even without local state so a reopened
    // running turn can be discovered. Historical terminal/idle results are
    // ignored unless this browser is actually waiting for a turn.
    if (!current) return;
    if (terminalTooEarlyForUnidentifiedTurn(current, status)) {
      // Keep polling. The new POST may still be uploading/routing, or its 202
      // may have been lost; adopting an older completed row would stop status
      // polling and wedge the real turn when it appears moments later.
      return;
    }
    if (
      current.workflowId &&
      status.workflow_id &&
      current.workflowId !== status.workflow_id
    ) {
      clearStreamingDraft();
    }

    if (status.status === 'completed') {
      // Completion means Temporal is done, but keep the composer gated until
      // GET messages supplies the canonical assistant row. The messages hook
      // resolves this finalizing state in the same tick it hydrates that row.
      upsertActiveTurn(sessionId, {
        phase: 'finalizing',
        turnId:
          status.identity_mode === 'legacy'
            ? undefined
            : (status.turn_id ?? current.turnId),
        identityMode: status.identity_mode ?? current.identityMode,
        workflowId: status.workflow_id ?? current.workflowId,
        runId: status.run_id ?? current.runId,
        updatedAt: status.updated_at,
        authoritativeCompletionWorkflowId:
          status.workflow_id ?? current.workflowId,
      });
      void queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
      return;
    }

    if (status.status === 'cancelled') {
      clearStreamingDraft();
      // Stop is a user-requested outcome, not a generation failure. Unlock
      // cleanly without showing the red retry strip.
      resolveActiveTurn(sessionId, 'done');
      void queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
      return;
    }

    if (status.status === 'failed' || status.status === 'timed_out') {
      clearStreamingDraft();
      resolveActiveTurn(sessionId, 'error');
      void queryClient.invalidateQueries({ queryKey: messagesKey(sessionId) });
      return;
    }

    // `idle` during the POST/visibility hand-off is not immediately evidence of
    // failure. Once the authoritative endpoint has remained idle beyond its
    // visibility grace *and* no workflow id was ever acknowledged, we know the
    // request never reached Temporal and can offer Retry without wedging.
    if (
      status.status === 'idle' &&
      !current.workflowId &&
      current.phase !== 'submitting' &&
      Date.now() - Date.parse(current.startedAt) >=
        SUBMISSION_VISIBILITY_GRACE_MS
    ) {
      clearStreamingDraft();
      resolveActiveTurn(
        sessionId,
        current.phase === 'cancelling' ? 'done' : 'error'
      );
      return;
    }

    // Unknown schemas, transiently idle known workflows, and the initial idle
    // hand-off stay recoverable. Browser silence by itself never unlocks.
    upsertActiveTurn(sessionId, {
      phase: recoveryPhase(current.phase),
    });
  }, [
    sessionId,
    query.data,
    query.dataUpdatedAt,
    queryClient,
    upsertActiveTurn,
    resolveActiveTurn,
    clearStreamingDraft,
    clearQueuedMessage,
  ]);
};
